import { useError } from '@hook/useError';
import { createContext, useContext, useState } from 'react';
import {
  ISpace,
  IVoteContextType,
  IVoteList,
  IVoteListData,
  IVoteResponseListData,
  IVoteStatistics,
  TCreateSpace,
  TCreateVote,
  TDeleteVote,
  TFetchSpace,
  TFetchVoteDetailInfo,
  TFetchVoteList,
  TFetchVoteResponse,
  TFetchVoteStatistics,
  TFetchVoteStatisticsForDashboard,
  TUpdateSpace,
  TgetUserVotePaticipations,
} from './types';
import { CLIENT_API } from '@api/services';

const VoteContext = createContext<IVoteContextType | undefined>(undefined);

export function VoteContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { handleError } = useError();

  const [space, setSpace] = useState<ISpace | null>(null);

  const [voteListData, setVoteListData] = useState<IVoteListData | null>(null);

  const [voteDetailInfo, setVoteDetailInfo] = useState<IVoteList | null>(null);

  const [voteStatistics, setVoteStatistics] = useState<IVoteStatistics[]>([]);

  const [voteResponseData, setVoteResponseData] =
    useState<IVoteResponseListData | null>(null);

  const fetchSpace: TFetchSpace = async (userId) => {
    try {
      const { data } =
        await CLIENT_API.VoteApi.findCreatedSpaceByUserId(userId);
      if (data) {
        setSpace(data);
        return true;
      } else {
        return false;
      }
    } catch (e) {
      handleError(e);
      return false;
    }
  };

  const createSpace: TCreateSpace = async (params) => {
    try {
      const {
        data: { id },
      } = await CLIENT_API.VoteApi.createSpace(params);

      if (id) {
        setSpace({
          id,
          ...params,
        });
        return true;
      }
      return false;
    } catch (e) {
      handleError(e);
      return false;
    }
  };

  const updateSpace: TUpdateSpace = async (spaceId, params) => {
    try {
      const {
        data: { id },
      } = await CLIENT_API.VoteApi.updateSpace(spaceId, params);

      if (id) {
        setSpace({
          id,
          ...params,
        });
        return true;
      }
      return false;
    } catch (e) {
      handleError(e);
      return false;
    }
  };

  const fetchVoteList: TFetchVoteList = async ({
    spaceId,
    status,
    requestPageNumber,
    limit,
    search,
  }) => {
    try {
      const { data } = await CLIENT_API.VoteApi.findVotes(spaceId, {
        requestPageNumber,
        limit,
        status,
        ...(search && { search }),
      });

      if (data) {
        setVoteListData(data);
      }
    } catch (e) {
      handleError(e);
    }
  };

  const createVote: TCreateVote = async (params) => {
    try {
      const {
        data: { id },
      } = await CLIENT_API.VoteApi.createVote(params);
      if (id) {
        return true;
      }
      return false;
    } catch (e) {
      handleError(e);
      return false;
    }
  };

  const fetchVoteDetailInfo: TFetchVoteDetailInfo = async (voteId) => {
    try {
      const { data } = await CLIENT_API.VoteApi.findVoteById(voteId);
      if (data) {
        setVoteDetailInfo(data);
      }
    } catch (e) {
      handleError(e);
    }
  };

  const fetchVoteStatisticsForDashboard: TFetchVoteStatisticsForDashboard =
    async (spaceId, limit, status) => {
      try {
        const {
          data: { data },
        } = await CLIENT_API.VoteApi.findVoteStatistics(spaceId, {
          limit,
          status,
        });

        const filterData = data.filter((item) => item.totalVoteCount !== 0);

        setVoteStatistics(
          filterData.map((item) => ({
            id: item.voteId,
            title: item.name,
            data: item.answers.map((answer) => ({
              label: answer.name,
              value: answer.voteCount,
            })),
          })),
        );
      } catch (e) {
        setVoteStatistics([]);
        handleError(e);
      }
    };

  const fetchVoteStatistics: TFetchVoteStatistics = async (voteId) => {
    try {
      const { data } = await CLIENT_API.VoteApi.findStatisticsById(voteId);
      const { data: voteInfo } = await CLIENT_API.VoteApi.findVoteById(
        data.voteId,
      );

      if (data.totalVoteCount === 0) return setVoteStatistics([]);

      setVoteStatistics([
        {
          id: voteId,
          title: voteInfo.name,
          data: data.answers.map((item) => ({
            label: item.name,
            value: item.voteCount,
          })),
        },
      ]);
    } catch (e) {
      handleError(e);
    }
  };

  const fetchVoteResponse: TFetchVoteResponse = async ({
    voteId,
    sortField,
    requestPageNumber,
    limit,
  }) => {
    try {
      const { data } = await CLIENT_API.VoteApi.findProgressById(voteId, {
        sortField,
        requestPageNumber,
        limit,
      });
      if (data) {
        setVoteResponseData(data);
      }
    } catch (e) {
      handleError(e);
    }
  };

  const deleteVote: TDeleteVote = async (voteId) => {
    try {
      const {
        data: { successCount },
      } = await CLIENT_API.VoteApi.deleteVote(voteId);
      if (successCount) {
        return true;
      }
      return false;
    } catch (e) {
      handleError(e);
      return false;
    }
  };

  const getUserVotePaticipations: TgetUserVotePaticipations = async (
    spaceId,
    requestPageNumber,
    userId,
    limit,
  ) => {
    try {
      const { data } = await CLIENT_API.VoteApi.findVoteParticipations(
        spaceId,
        {
          requestPageNumber,
          userId,
          limit,
        },
      );
      if (data) {
        setVoteResponseData(data);
      }
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <VoteContext.Provider
      value={{
        space,
        fetchSpace,
        createSpace,
        updateSpace,
        fetchVoteList,
        voteListData,
        createVote,
        fetchVoteDetailInfo,
        voteDetailInfo,
        fetchVoteStatistics,
        voteStatistics,
        fetchVoteResponse,
        voteResponseData,
        deleteVote,
        fetchVoteStatisticsForDashboard,
        getUserVotePaticipations,
      }}
    >
      {children}
    </VoteContext.Provider>
  );
}

export const useVoteContext = () => {
  const context = useContext(VoteContext);
  if (!context) {
    throw new Error('useVoteContext must be used within a VoteContextProvider');
  }
  return context;
};
